import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
} from '@tanstack/react-router';
import { AppShell } from '@/components/AppShell';
import { DashboardPage } from '@/routes/DashboardPage';
import { GapsPage } from '@/features/gaps/GapsPage';
import { RsiPage } from '@/features/rsi/RsiPage';
import { TimeWindowsPage } from '@/features/time-windows/TimeWindowsPage';
import { KnotsPage } from '@/features/knots/KnotsPage';
import { FibonacciPage } from '@/features/fibonacci/FibonacciPage';
import { ResearchPage } from '@/features/fibonacci/ResearchPage';
import { VolumePage } from '@/features/volume/VolumePage';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AlertsPage } from '@/features/alerts/AlertsPage';

/**
 * Routes are defined in code rather than generated from the filesystem.
 *
 * With a handful of screens this avoids the codegen step and the generated
 * routeTree file entirely, while keeping TanStack Router's type-safe navigation.
 *
 * Auth is NOT enforced here. `AuthGate` in App.tsx decides between the login screen
 * and the app, so an unauthenticated visitor never mounts a route at all — which
 * keeps the redirect logic in one place instead of spread across route guards.
 */

const rootRoute = createRootRoute({
  component: () => (
    <AppShell>
      <Outlet />
    </AppShell>
  ),
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: DashboardPage,
});

const gapsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/gaps',
  component: GapsPage,
});

const rsiRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/rsi',
  component: RsiPage,
});

const timeWindowsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/time-windows',
  component: TimeWindowsPage,
});

const knotsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/knots',
  component: KnotsPage,
});

const fibonacciRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fibonacci',
  component: FibonacciPage,
});

const fibonacciResearchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/fibonacci/research',
  component: ResearchPage,
});

const volumeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/volume',
  component: VolumePage,
});

const alertsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/alerts',
  component: AlertsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
});

const routeTree = rootRoute.addChildren([
  dashboardRoute,
  gapsRoute,
  rsiRoute,
  timeWindowsRoute,
  knotsRoute,
  fibonacciRoute,
  fibonacciResearchRoute,
  volumeRoute,
  alertsRoute,
  settingsRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // A private tool on a fast connection: no need for a loading flash on nav.
  defaultPreloadStaleTime: 0,
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
